<template>
  <el-skeleton :loading="skeletonLoadingState" animated>
    <template #template>
      <el-skeleton-item />
    </template>
    <template #default>
      <div class="tw:group stats-section share">
        <div class="tw:group-hover:opacity-100 tw:opacity-0 stats-section-gradient" />
        <div class="stats-section-gradient" />

        <div class="stats-section-item">
          <div class="stats-section-title">{{ $t('storageMobileTotalPearlShare') }}</div>
          <div class="token-data">
            <div class="text-value">
              <span>{{ formattedMySharePercent }}%</span>
            </div>
          </div>
        </div>

        <div class="stats-section-item">
          <div class="stats-section-title">{{ $t('appStatisticAPR') }}</div>
          <div class="token-data">
            <div class="text-value apr">
              <span>{{ formattedAprPercent }}%</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </el-skeleton>
</template>

<script setup lang="ts">
import { BigNumber } from 'bignumber.js';

const isLoadingUpdateDataStatisticsInjected = inject<Ref<boolean>>('isLoadingUpdateDataStatistics');
const stakedPercentInjected = inject<Ref<string>>('stakedPercent');
const aprPercentInjected = inject<Ref<string>>('aprPercent');

const formattedMySharePercent = computed(() => {
  if (!stakedPercentInjected) return '0';

  const value = BigNumber(stakedPercentInjected.value);
  if (value.isLessThan(0.01) && value.isGreaterThan(0)) {
    return '<0.01';
  }

  return value.toFormat(2);
});

const formattedAprPercent = computed(() => {
  if (!aprPercentInjected) return '0';
  return BigNumber(aprPercentInjected.value).toFormat(2);
});

const skeletonLoadingState = computed(() => {
  const hasNoStakedPercent = !stakedPercentInjected?.value || stakedPercentInjected.value === '';
  const hasNoAprPercent = !aprPercentInjected?.value || aprPercentInjected.value === '';

  const hasNoData = hasNoStakedPercent && hasNoAprPercent;

  return hasNoData && isLoadingUpdateDataStatisticsInjected?.value;
});
</script>

<style scoped lang="scss">
.stats-section {
  background-image: radial-gradient(circle at 100% 100%, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0) 50%);
  border: 2px solid #133442;
  border-radius: 12px;
  flex-direction: column;
  flex: 1;
  align-items: flex-start;
  padding: 12px 16px 16px;
  font-size: 24px;
  transition: all 0.3s;
  display: flex;
  position: relative;
  overflow: hidden;
  min-height: 370px;
  min-width: 32.35%;

  @media screen and (max-width: 991px) {
    font-size: 16px;
  }

  @media screen and (max-width: 560px) {
    width: 100%;
    flex: none;
    align-items: center;
    min-height: unset;
  }
}

.share {
  background-image: linear-gradient(transparent, #00000040 50%, #00000080);
}

.stats-section-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;

  @media screen and (max-width: 560px) {
    font-size: 18px;
    gap: 0px;
    align-items: center;
  }
}

.stats-section-title {
  font-size: 24px;
  font-weight: 400;
  color: var(--second-text-color);

  @media screen and (max-width: 991px) {
    font-size: 16px;
  }
}

.stats-section-gradient {
  z-index: 0;
  opacity: 0;
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(88, 159, 100, 0.1) 60%, rgba(140, 253, 159, 0.2));
  transition: opacity 0.3s;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  &:hover {
    opacity: 1;
  }
}

.token-data {
  width: 100%;
  margin-bottom: 16px;
  line-height: 1;

  @media screen and (max-width: 560px) {
    display: flex;
    justify-content: center;
    margin-bottom: 0;
    height: 26px;
  }
}

.text-value {
  height: 40px;
  color: var(--main-text-color);
  font-size: 30px;

  @media screen and (max-width: 991px) {
    font-size: 24px;
  }

  @media screen and (max-width: 767px) {
    font-size: 18px;
  }
}

.apr {
  color: var(--yellow);
}
</style>
